/**
 * HotelBookingPage — final booking step (themed to match BookingPage.jsx
 * for visual parity across Flight and Hotel verticals).
 *
 * Theme contract mirrored from `BookingPage.jsx`:
 *   - Page bg: gradient-to-br from-blue-50 via-white to-teal-50
 *   - Section cards: white rounded-2xl shadow-sm border + gradient header
 *     [#1B375B → #659A9B] with white heading
 *   - Right rail: lg:w-96 sticky top-20 card stack
 *   - Auth chip top-right with gradient avatar + wallet/phone meta
 *   - Pax card: gray-50 inner panel with "Use Saved Traveller" pill
 *     (border #659A9B, hover bg #659A9B/5)
 *
 * Functional contract preserved from Slice 3.6:
 *   - URL `?rooms=...` seeds room composition exactly
 *   - Per-option PAN/Passport/min-age conditions surfaced live
 *   - Saved-traveller picker (B2C → /api/user/passengers, Agent →
 *     /api/agent/dashboard/travellers) with search-by-name/passport
 *   - Save-this-traveller toggle per row
 *   - Agent payment-mode card (Wallet / Credit / Gateway / Hold)
 *   - Hold flow status mutation
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Mail, Phone, AlertTriangle, Clock, Wallet, CreditCard,
  Globe, User, UserPlus, Baby, ChevronDown, BookmarkPlus, ShieldCheck,
  BadgeCheck, IdCard, FileText, LogIn, LogOut, LogIn as Login,
} from 'lucide-react';
import Footer from '../components/Footer';
import BottomNav from '../components/BottomNav';
import { useAgentAuth } from '../context/AgentAuthContext';
import { useAuth } from '../context/AuthContext';
import AgentBalanceBadge from '../components/agent/AgentBalanceBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const _parseRoomsParam = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    if (!Array.isArray(parsed) || !parsed.length) return null;
    return parsed.map((r) => ({
      adults: Math.max(1, parseInt(r.numberOfAdults || 0, 10) || 1),
      children: Math.max(0, parseInt(r.numberOfChild || 0, 10) || 0),
      childAges: Array.isArray(r.childAge) ? r.childAge : [],
    }));
  } catch { return null; }
};

const _emptyPax = (isChild, age = null) => ({
  ti: isChild ? 'Master' : 'Mr',
  fN: '', lN: '',
  pt: isChild ? 'CHILD' : 'ADULT',
  age,
  pan: '', pNum: '', nationality: 'IN',
  saveToMyList: false,
  savedFromId: null,
});

const _seedRooms = (roomsParam) => {
  const parsed = _parseRoomsParam(roomsParam);
  if (!parsed) return [{ travellerInfo: [_emptyPax(false)] }];
  return parsed.map((r) => ({
    travellerInfo: [
      ...Array.from({ length: r.adults }, () => _emptyPax(false)),
      ...Array.from({ length: r.children }, (_, i) => _emptyPax(true, r.childAges[i] || null)),
    ],
  }));
};

// ── Saved-traveller picker (themed like flight booking) ──
const SavedTravellerDropdown = ({ travellers, pax, isAgent, onSelect, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = React.useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const items = useMemo(() => {
    if (!q) return travellers;
    const needle = q.toLowerCase();
    return travellers.filter((t) => {
      const name = `${t.first_name || ''} ${t.last_name || ''}`.toLowerCase();
      const pp = (t.passport_no || t.passport_number || '').toLowerCase();
      return name.includes(needle) || pp.includes(needle);
    });
  }, [travellers, q]);
  if (!travellers || travellers.length === 0) return null;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setQ(''); }}
        className="text-sm px-3 py-1.5 border border-[#659A9B] rounded-lg bg-white text-[#1B375B] font-semibold hover:bg-[#659A9B]/5 transition-colors flex items-center gap-2"
        data-testid={testId}
      >
        <User className="w-4 h-4" />
        {pax.savedFromId ? 'Change' : 'Use Saved'} {isAgent ? 'Traveller' : 'Passenger'}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 max-h-96 flex flex-col" data-testid={`${testId}-list`}>
          <div className="px-3 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-t-xl">
            <p className="text-xs font-semibold">Select {pax.pt === 'CHILD' ? 'CHILD' : 'ADULT'} {isAgent ? 'Traveller' : 'Passenger'}</p>
          </div>
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or passport…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">No matching travellers</div>
            ) : items.map((t) => {
              const name = `${t.title || ''} ${t.first_name || ''} ${t.last_name || ''}`.trim();
              const sub = [
                t.dob && `DOB: ${t.dob}`,
                (t.passport_no || t.passport_number) && `PP: ${t.passport_no || t.passport_number}`,
                t.pan && `PAN: ${t.pan}`,
              ].filter(Boolean).join(' · ');
              const isSelected = pax.savedFromId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { onSelect(t); setOpen(false); }}
                  className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-0 ${isSelected ? 'bg-[#659A9B]/20 hover:bg-[#659A9B]/30' : 'hover:bg-[#659A9B]/10'}`}
                  data-testid={`${testId}-item-${t.id}`}
                >
                  <p className="font-bold text-sm text-[#1B375B] truncate">{name || '—'}</p>
                  {sub && <p className="text-xs text-gray-500 truncate mt-0.5">{sub}</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const HotelBookingPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const hotelId = params.get('hotelId');
  const optionId = params.get('optionId');
  const bookingId = params.get('bookingId');
  const totalAmount = parseFloat(params.get('totalAmount') || '0');
  const fareChanged = params.get('fareChanged') === '1';
  const oldFare = params.get('oldFare');
  const newFare = params.get('newFare');
  const roomsParam = params.get('rooms');

  const { agent, isAuthenticated: isAgent, logout: agentLogout, refreshAgentBalances } = useAgentAuth();
  const { isAuthenticated: isB2C, user: b2cUser, logout: b2cLogout } = useAuth();

  useEffect(() => {
    if (!agent?.id) return;
    refreshAgentBalances();
  }, [agent?.id]);

  const [rooms, setRooms] = useState(() => _seedRooms(roomsParam));
  const [email, setEmail] = useState(() => b2cUser?.email || '');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('+91');
  const [acceptedFareChange, setAcceptedFareChange] = useState(!fareChanged);
  const [paymentMode, setPaymentMode] = useState(isAgent ? 'wallet' : 'gateway');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [conditions, setConditions] = useState({ panRequired: false, passportRequired: false, minAge: null, hotelName: '', loaded: false });
  const [savedTravellers, setSavedTravellers] = useState([]);
  // Phase H6.C — per-room metadata from the selected option (room type, bed, meal).
  // Lines up 1:1 with the local `rooms` state by index.
  const [roomsInOption, setRoomsInOption] = useState([]);
  const [optionMeta, setOptionMeta] = useState({ mealBasis: null, refundable: false });

  // Phase H7 — Watchdog state: live bookingId/total (mutated by /review
  // re-checks), staleness timer, sold-out flag, fare-change banner.
  const [liveBookingId, setLiveBookingId] = useState(bookingId);
  const [liveTotalAmount, setLiveTotalAmount] = useState(parseFloat(params.get('totalAmount') || '0'));
  const [rateValidatedAt, setRateValidatedAt] = useState(() => new Date());
  const [revalidating, setRevalidating] = useState(false);
  const [revalidateError, setRevalidateError] = useState('');
  const [watchdogSoldout, setWatchdogSoldout] = useState(false);
  const [watchdogFareChange, setWatchdogFareChange] = useState(null);  // {oldFare, newFare}

  // Phase H7 — Idempotency key. Generated once per booking attempt and
  // persisted to sessionStorage by supplier bookingId so a page refresh
  // (or accidental double-submit) reuses the SAME key → backend returns
  // the original booking instead of double-billing.
  const idempotencyKey = useMemo(() => {
    if (!bookingId) return null;
    const storageKey = `hotel_book_idem:${bookingId}`;
    try {
      const cached = window.sessionStorage.getItem(storageKey);
      if (cached) return cached;
    } catch { /* sessionStorage may be blocked */ }
    const fresh = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `hotbk-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    try { window.sessionStorage.setItem(storageKey, fresh); } catch { /* ignore */ }
    return fresh;
  }, [bookingId]);

  const totalPax = useMemo(() => {
    let a = 0, c = 0;
    for (const r of rooms) for (const t of r.travellerInfo) {
      if (t.pt === 'CHILD') c++; else a++;
    }
    return { adults: a, children: c, rooms: rooms.length };
  }, [rooms]);

  useEffect(() => {
    if (!hotelId || !optionId) return;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/hotels/${encodeURIComponent(hotelId)}/details`, { timeout: 30000 });
        const h = res.data?.hotel;
        if (!h) return;
        const opt = (h.options || []).find((o) => o.id === optionId);
        setConditions({
          panRequired: !!opt?.pan_required,
          passportRequired: !!opt?.passport_required,
          minAge: h.checkin_time?.minAge || null,
          hotelName: h.name || '',
          loaded: true,
        });
        // Phase H6.C — surface per-room metadata for the selected option so
        // the booking form can label each Room {N} with its actual room type.
        if (opt) {
          setRoomsInOption(Array.isArray(opt.rooms_in_option) ? opt.rooms_in_option : []);
          setOptionMeta({
            mealBasis: opt.meal_basis || opt.mb || null,
            refundable: !!opt.is_refundable,
            freeUntil: opt.free_cancellation_until || null,
            roomTypeLabel: opt.room_type_group_label || opt.name || null,
          });
        }
      } catch { /* keep defaults */ }
    })();
  }, [hotelId, optionId]);

  useEffect(() => {
    (async () => {
      try {
        if (isAgent && agent?.id) {
          const r = await axios.get(`${BACKEND_URL}/api/agent/dashboard/travellers?limit=100`, { withCredentials: true });
          setSavedTravellers(r.data?.travellers || []);
        } else if (isB2C) {
          const r = await axios.get(`${BACKEND_URL}/api/user/passengers`, { withCredentials: true });
          setSavedTravellers(r.data?.passengers || []);
        }
      } catch { setSavedTravellers([]); }
    })();
  }, [isAgent, isB2C, agent?.id]);

  // Phase H7 — Re-validation watchdog. Calls /api/hotels/review every 5 min
  // to detect SOLDOUT or FAREALERT before user reaches Pay. Tripjack's review
  // session expires (typically 10-15 min); re-checking keeps `bookingId` fresh.
  const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000;  // 5 minutes
  const revalidate = useCallback(async () => {
    if (!hotelId || !optionId) return;
    setRevalidating(true);
    setRevalidateError('');
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/hotels/review`,
        { hotelId, optionId },
        { timeout: 45000 },
      );
      const body = r.data || {};
      if (!body.success) {
        setRevalidateError('Could not re-validate this rate. Please retry.');
        return;
      }
      if (body.soldout) {
        setWatchdogSoldout(true);
        return;
      }
      // Compare new total to current — if drift > ₹1 INR, flag fare change.
      const newTotal = parseFloat(body.totalAmount || 0);
      const prevTotal = parseFloat(liveTotalAmount || 0);
      if (newTotal > 0 && Math.abs(newTotal - prevTotal) > 1) {
        setWatchdogFareChange({
          oldFare: prevTotal,
          newFare: newTotal,
          acceptedAt: null,
        });
      }
      // Always refresh bookingId + total (supplier rotates IDs per /review).
      if (body.bookingId) setLiveBookingId(body.bookingId);
      if (newTotal > 0) setLiveTotalAmount(newTotal);
      setRateValidatedAt(body.rate_validated_at ? new Date(body.rate_validated_at) : new Date());
    } catch (e) {
      setRevalidateError(e.response?.data?.detail?.message || 'Network error while re-validating rate.');
    } finally {
      setRevalidating(false);
    }
  }, [hotelId, optionId, liveTotalAmount]);

  // Auto-revalidate every 5 min (only while the page is open and not sold out).
  useEffect(() => {
    if (!hotelId || !optionId || watchdogSoldout) return;
    const id = setInterval(() => { revalidate(); }, REVALIDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hotelId, optionId, watchdogSoldout, revalidate, REVALIDATE_INTERVAL_MS]);

  // Accepting the new fare clears the banner + commits the new amount.
  const acceptWatchdogFareChange = () => setWatchdogFareChange((f) => f ? { ...f, acceptedAt: new Date() } : null);

  if (!bookingId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex flex-col items-center justify-center p-6">
        <p className="text-red-600 font-bold mb-3">Booking session expired. Please start over.</p>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-[#1B375B] text-white rounded-lg text-sm font-bold">Home</button>
      </div>
    );
  }

  const updatePax = (rIdx, pIdx, patch) => {
    setRooms((prev) => prev.map((r, i) => {
      if (i !== rIdx) return r;
      return { ...r, travellerInfo: r.travellerInfo.map((t, j) => (j === pIdx ? { ...t, ...patch } : t)) };
    }));
  };

  const applySaved = (rIdx, pIdx, saved) => {
    updatePax(rIdx, pIdx, {
      ti: saved.title || 'Mr',
      fN: saved.first_name || '',
      lN: saved.last_name || '',
      pan: saved.pan || '',
      pNum: saved.passport_no || saved.passport_number || '',
      nationality: saved.nationality || saved.passport_nationality || 'IN',
      savedFromId: saved.id,
    });
  };

  const validate = () => {
    if (fareChanged && !acceptedFareChange) return 'Please acknowledge the fare change to continue.';
    for (const [rIdx, room] of rooms.entries()) {
      for (const [pIdx, t] of room.travellerInfo.entries()) {
        const label = t.pt === 'CHILD' ? 'Child' : 'Adult';
        const slot = `Room ${rIdx + 1}, ${label} ${pIdx + 1}`;
        if (!t.fN || t.fN.trim().length < 2) return `${slot}: please enter a valid first name.`;
        if (!t.lN || t.lN.trim().length < 2) return `${slot}: please enter a valid last name.`;
        if (conditions.panRequired && t.pt === 'ADULT') {
          const pan = (t.pan || '').toUpperCase().trim();
          if (!pan) return `${slot}: PAN number is required for this hotel.`;
          if (!PAN_REGEX.test(pan)) return `${slot}: PAN format invalid (expected: ABCDE1234F).`;
        }
        if (conditions.passportRequired) {
          const pp = (t.pNum || '').trim();
          if (!pp || pp.length < 6) return `${slot}: passport number is required (min 6 chars).`;
        }
      }
    }
    if (!email || !email.includes('@')) return 'Please enter a valid email.';
    if (!phone || phone.replace(/\D/g, '').length < 7) return 'Please enter a valid phone number.';
    return '';
  };

  const saveNewTravellers = async () => {
    for (const room of rooms) {
      for (const t of room.travellerInfo) {
        if (!t.saveToMyList || t.savedFromId) continue;
        try {
          if (isAgent) {
            await axios.post(`${BACKEND_URL}/api/agent/dashboard/travellers`, null, {
              params: {
                title: t.ti, first_name: t.fN.trim(), last_name: t.lN.trim(),
                dob: '2000-01-01',
                ...(t.pNum ? { passport_number: t.pNum.trim() } : {}),
                ...(t.nationality ? { passport_nationality: t.nationality } : {}),
              },
              withCredentials: true,
            });
          } else if (isB2C) {
            await axios.post(`${BACKEND_URL}/api/user/passengers`, null, {
              params: {
                title: t.ti, first_name: t.fN.trim(), last_name: t.lN.trim(),
                gender: t.ti === 'Mrs' || t.ti === 'Ms' ? 'Female' : 'Male',
                ...(t.pNum ? { passport_no: t.pNum.trim() } : {}),
                ...(t.nationality ? { nationality: t.nationality } : {}),
              },
              withCredentials: true,
            });
          }
        } catch { /* best-effort */ }
      }
    }
  };

  const handleSubmit = async () => {
    setError('');
    // Phase H7 — watchdog gating: hard-block if option is sold out
    // or if there's an un-accepted fare drift since the last revalidate.
    if (watchdogSoldout) {
      setError('This option just sold out. Please go back and pick another.');
      return;
    }
    if (watchdogFareChange && !watchdogFareChange.acceptedAt) {
      setError('Fare has changed. Please accept the new price before continuing.');
      return;
    }
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      saveNewTravellers().catch(() => {});
      const payload = {
        bookingId: liveBookingId || bookingId,
        hotelId,
        roomTravellerInfo: rooms.map((r) => ({
          travellerInfo: r.travellerInfo.map((t) => ({
            ti: t.ti, fN: t.fN.trim(), lN: t.lN.trim(),
            pt: t.pt,
            ...(t.pan ? { pan: t.pan.toUpperCase().trim() } : {}),
            ...(t.pNum ? { pNum: t.pNum.toUpperCase().trim() } : {}),
          })),
        })),
        deliveryInfo: { emails: [email.trim()], contacts: [phone.replace(/\D/g, '')], code: [dialCode] },
        baseAmount: liveTotalAmount || totalAmount,
        user_type: isAgent ? 'agent' : 'b2c',
        ...(isAgent && agent?.id ? { agent_id: agent.id, user_id: agent.id } : {}),
        payment_mode: paymentMode,
        hotel_meta: {
          hotelId,
          optionId,
          // Phase H6.C — persist per-room metadata so the booking-success page,
          // admin/agent panels, and GST invoice email can show the exact rooms.
          ...(conditions.hotelName ? { hotelName: conditions.hotelName } : {}),
          ...(params.get('cityname') ? { city: params.get('cityname') } : {}),
          ...(params.get('checkin') ? { checkin: params.get('checkin') } : {}),
          ...(params.get('checkout') ? { checkout: params.get('checkout') } : {}),
          ...(roomsInOption.length ? { rooms_in_option: roomsInOption } : {}),
          ...(optionMeta.roomTypeLabel ? { roomTypeLabel: optionMeta.roomTypeLabel } : {}),
          ...(optionMeta.mealBasis ? { mealBasis: optionMeta.mealBasis } : {}),
          refundable: !!optionMeta.refundable,
        },
        // Phase H7 — Idempotency key (replay-safe duplicate submission guard).
        ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
      };
      const res = await axios.post(`${BACKEND_URL}/api/hotels/book`, payload, { timeout: 60000 });
      const body = res.data || {};
      if (!body.success) { setError('Booking failed. Please retry.'); return; }

      if (body.requires_gateway_payment) {
        const { openRazorpayCheckout } = await import('../utils/razorpayCheckout');
        await openRazorpayCheckout({
          createOrderPayload: {
            context: 'hotel',
            receipt: body.receipt || body.order_id,
            amount: Number(body.amount),
            agent_id: isAgent ? agent?.id : null,
            metadata: { booking_id: body.booking_id },
          },
          prefill: {
            name: rooms[0]?.travellerInfo?.[0] ? `${rooms[0].travellerInfo[0].fN} ${rooms[0].travellerInfo[0].lN}` : '',
            email: email.trim(),
            contact: phone.replace(/\D/g, ''),
          },
          description: 'Hotel booking payment',
          onVerified: () => {
            navigate(`/hotels/booking-success?bookingId=${encodeURIComponent(body.booking_id)}`);
          },
        });
        return;
      }

      navigate(`/hotels/booking-success?bookingId=${encodeURIComponent(body.booking_id)}`);
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.response?.data?.message || 'Booking failed. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 pb-24 md:pb-12" data-testid="hotel-booking-page">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">

        {/* ── Back button + Auth chip (mirrors flight booking) ── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all shadow-sm hover:shadow-md"
            data-testid="hotel-booking-back"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Hotel
          </button>

          {isAgent && agent ? (
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Agent</p>
                  <p className="text-sm font-bold text-gray-800">{agent.full_name}</p>
                </div>
              </div>
              <AgentBalanceBadge agent={agent} variant="compact" />
              <button onClick={agentLogout} className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1 ml-2">
                <LogOut className="w-3 h-3" /> Logout
              </button>
            </div>
          ) : isB2C && b2cUser ? (
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1B375B] to-[#659A9B] flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{b2cUser.full_name}</p>
                  <p className="text-xs text-gray-500">{b2cUser.phone ? `+91 ${b2cUser.phone}` : b2cUser.email}</p>
                </div>
              </div>
              <button onClick={b2cLogout} className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1">
                <LogOut className="w-3 h-3" /> Logout
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              data-testid="hotel-booking-login-cta"
            >
              <LogIn className="w-4 h-4" />
              Login to Save Travellers
            </button>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* ── Left column ── */}
          <div className="flex-1 space-y-6 min-w-0">

            {/* Hotel summary card (mirrors flight-details card pattern) */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4 flex items-center justify-between">
                <h2 className="text-white font-bold">Your Hotel Booking</h2>
                <span className="px-2.5 py-0.5 bg-white/15 text-white text-xs font-bold rounded-full" data-testid="hotel-booking-pax-summary">
                  {totalPax.rooms} room{totalPax.rooms > 1 ? 's' : ''} · {totalPax.adults} adult{totalPax.adults !== 1 ? 's' : ''}{totalPax.children ? ` · ${totalPax.children} child` : ''}
                </span>
              </div>
              {conditions.hotelName && (
                <div className="px-6 py-3 text-sm">
                  <p className="font-bold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>{conditions.hotelName}</p>
                </div>
              )}
            </div>

            {/* Fare changed alert */}
            {fareChanged && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-2" data-testid="hotel-booking-fare-alert">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-800">Fare changed since you selected</p>
                  <p className="text-xs text-amber-700 mt-1">Was ₹{Number(oldFare).toLocaleString('en-IN')} → now ₹{Number(newFare).toLocaleString('en-IN')}</p>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input type="checkbox" checked={acceptedFareChange} onChange={(e) => setAcceptedFareChange(e.target.checked)} data-testid="hotel-booking-accept-fare" className="w-4 h-4 rounded" />
                    <span className="text-xs font-semibold text-amber-800">I accept the new fare</span>
                  </label>
                </div>
              </div>
            )}

            {/* Phase H7 — Watchdog: SOLDOUT banner (blocks the booking) */}
            {watchdogSoldout && (
              <div className="bg-rose-50 border border-rose-300 rounded-xl p-4 flex items-start gap-2" data-testid="hotel-booking-watchdog-soldout">
                <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-rose-800">This room just sold out</p>
                  <p className="text-xs text-rose-700 mt-1">While you were filling traveller details, the hotel ran out of this option. Please go back and pick another.</p>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700"
                    data-testid="hotel-booking-watchdog-back"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to options
                  </button>
                </div>
              </div>
            )}

            {/* Phase H7 — Watchdog: FARE CHANGE banner (re-validate triggered mid-session) */}
            {watchdogFareChange && !watchdogFareChange.acceptedAt && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-2" data-testid="hotel-booking-watchdog-farechange">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-800">Rate updated — fare changed since you opened this page</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Was <span className="line-through">₹{Number(watchdogFareChange.oldFare || 0).toLocaleString('en-IN')}</span>
                    {' '}→ <span className="font-extrabold">₹{Number(watchdogFareChange.newFare || 0).toLocaleString('en-IN')}</span>
                    {' '}({watchdogFareChange.newFare > watchdogFareChange.oldFare ? '+' : ''}₹{Number(watchdogFareChange.newFare - watchdogFareChange.oldFare).toLocaleString('en-IN', { maximumFractionDigits: 0 })})
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={acceptWatchdogFareChange}
                      data-testid="hotel-booking-watchdog-accept"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700"
                    >
                      Accept new fare & continue
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-amber-300 text-amber-800 rounded-lg text-xs font-bold hover:bg-amber-50"
                    >
                      Back to options
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Phase H7 — Subtle rate-freshness chip (always visible while watchdog is healthy) */}
            {!watchdogSoldout && !watchdogFareChange && rateValidatedAt && (
              <div className="flex items-center justify-between text-[11px] text-gray-500 px-1" data-testid="hotel-booking-rate-freshness">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3 text-emerald-600" />
                  Rate verified at {rateValidatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {revalidating && <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />}
                </span>
                <button
                  type="button"
                  onClick={revalidate}
                  disabled={revalidating}
                  data-testid="hotel-booking-rate-recheck"
                  className="text-[11px] text-[#659A9B] font-bold hover:underline disabled:opacity-50"
                >
                  Re-check now
                </button>
              </div>
            )}
            {revalidateError && (
              <p className="text-[11px] text-rose-600 px-1">{revalidateError}</p>
            )}

            {/* Required-docs banner */}
            {conditions.loaded && (conditions.panRequired || conditions.passportRequired || conditions.minAge) && (
              <div className="bg-white rounded-2xl shadow-sm border border-blue-200 overflow-hidden" data-testid="hotel-booking-conditions">
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-white" />
                  <h3 className="text-white font-bold text-sm">Required documents for check-in</h3>
                </div>
                <ul className="px-6 py-4 text-sm text-gray-700 space-y-1.5">
                  {conditions.panRequired && <li className="flex items-start gap-2"><IdCard className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" /><span><strong>PAN card</strong> required for each adult guest (Indian PAN format).</span></li>}
                  {conditions.passportRequired && <li className="flex items-start gap-2"><FileText className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" /><span><strong>Passport number</strong> required for each guest.</span></li>}
                  {conditions.minAge && <li className="flex items-start gap-2"><User className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /><span>Minimum guest age: <strong>{Math.round(conditions.minAge)} years</strong>.</span></li>}
                </ul>
              </div>
            )}

            {/* Rooms */}
            {rooms.map((room, rIdx) => {
              const adultCount = room.travellerInfo.filter((t) => t.pt === 'ADULT').length;
              const childCount = room.travellerInfo.length - adultCount;
              // Phase H6.C — pull this room slot's supplier metadata (room type / bed / meal)
              const ri = roomsInOption[rIdx] || null;
              const _rType = ri?.rt || ri?.rc || null;
              const _bt = ri?.bt || ri?.bedType || null;
              const _mb = ri?.mb || null;
              return (
                <div key={rIdx} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-testid={`hotel-booking-room-${rIdx}`}>
                  <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-3">
                    <div className="flex items-center justify-between flex-wrap gap-1">
                      <div className="min-w-0">
                        <h2 className="text-white font-bold leading-tight">Room {rIdx + 1}{_rType ? <span className="font-normal text-white/85"> · {_rType}</span> : null}</h2>
                        {(_bt || _mb) && (
                          <p className="text-[11px] text-white/75 mt-0.5" data-testid={`hotel-booking-room-meta-${rIdx}`}>
                            {[_bt, _mb && (Array.isArray(_mb) ? _mb.join(', ') : _mb)].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                      <span className="text-white/85 text-xs font-semibold whitespace-nowrap">
                        {adultCount} Adult{adultCount !== 1 ? 's' : ''}{childCount ? ` + ${childCount} Child${childCount !== 1 ? 'ren' : ''}` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="space-y-6">
                      {room.travellerInfo.map((t, pIdx) => {
                        const isChild = t.pt === 'CHILD';
                        const needsPan = conditions.panRequired && !isChild;
                        const needsPassport = conditions.passportRequired;
                        const slotIdx = isChild ? pIdx - adultCount + 1 : pIdx + 1;
                        const slotLabel = `${isChild ? 'CHILD' : 'ADULT'} ${slotIdx}`;
                        return (
                          <div key={pIdx} className="border border-gray-200 rounded-xl p-5 bg-gray-50" data-testid={`hotel-booking-pax-${rIdx}-${pIdx}`}>
                            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                              <h4 className="font-bold text-[#1B375B] flex items-center gap-2">
                                {isChild ? <Baby className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                {slotLabel}
                                {isChild && t.age != null && (
                                  <span className="ml-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Age {t.age}</span>
                                )}
                                {t.savedFromId && (
                                  <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                    <BadgeCheck className="w-3 h-3" /> From your list
                                  </span>
                                )}
                              </h4>
                              {((isB2C || isAgent) && savedTravellers.length > 0) && (
                                <SavedTravellerDropdown
                                  travellers={savedTravellers}
                                  pax={t}
                                  isAgent={isAgent}
                                  onSelect={(saved) => applySaved(rIdx, pIdx, saved)}
                                  testId={`hotel-booking-saved-picker-${rIdx}-${pIdx}`}
                                />
                              )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr_1fr] gap-3 mb-3">
                              <div>
                                <label className="text-xs font-semibold text-gray-700 mb-1 block">Title</label>
                                <select
                                  value={t.ti}
                                  onChange={(e) => updatePax(rIdx, pIdx, { ti: e.target.value })}
                                  data-testid={`hotel-booking-title-${rIdx}-${pIdx}`}
                                  disabled={isChild}
                                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#659A9B] disabled:bg-gray-100"
                                >
                                  {isChild ? (
                                    <>
                                      <option value="Master">Master</option>
                                      <option value="Miss">Miss</option>
                                    </>
                                  ) : (
                                    <>
                                      <option value="Mr">Mr</option>
                                      <option value="Mrs">Mrs</option>
                                      <option value="Ms">Ms</option>
                                    </>
                                  )}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-700 mb-1 block">First Name *</label>
                                <input
                                  type="text"
                                  placeholder="First name (as per ID)"
                                  value={t.fN}
                                  onChange={(e) => updatePax(rIdx, pIdx, { fN: e.target.value, savedFromId: null })}
                                  data-testid={`hotel-booking-fn-${rIdx}-${pIdx}`}
                                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-700 mb-1 block">Last Name *</label>
                                <input
                                  type="text"
                                  placeholder="Last name (as per ID)"
                                  value={t.lN}
                                  onChange={(e) => updatePax(rIdx, pIdx, { lN: e.target.value, savedFromId: null })}
                                  data-testid={`hotel-booking-ln-${rIdx}-${pIdx}`}
                                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                                />
                              </div>
                            </div>

                            {(needsPan || needsPassport) && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                {needsPan && (
                                  <div>
                                    <label className="text-xs font-semibold text-rose-700 mb-1 flex items-center gap-1"><IdCard className="w-3 h-3" /> PAN Number *</label>
                                    <input
                                      type="text"
                                      placeholder="ABCDE1234F"
                                      value={t.pan}
                                      onChange={(e) => updatePax(rIdx, pIdx, { pan: e.target.value.toUpperCase() })}
                                      maxLength={10}
                                      data-testid={`hotel-booking-pan-${rIdx}-${pIdx}`}
                                      className="w-full px-3 py-2 text-sm rounded-lg border border-rose-300 bg-rose-50/30 focus:outline-none focus:ring-2 focus:ring-rose-400 font-mono uppercase"
                                      aria-required="true"
                                    />
                                  </div>
                                )}
                                {needsPassport && (
                                  <div>
                                    <label className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Passport Number *</label>
                                    <input
                                      type="text"
                                      placeholder="Passport number"
                                      value={t.pNum}
                                      onChange={(e) => updatePax(rIdx, pIdx, { pNum: e.target.value.toUpperCase() })}
                                      data-testid={`hotel-booking-passport-${rIdx}-${pIdx}`}
                                      className="w-full px-3 py-2 text-sm rounded-lg border border-blue-300 bg-blue-50/30 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono uppercase"
                                      aria-required="true"
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                            {(isB2C || isAgent) && !t.savedFromId && (
                              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer mt-1 select-none">
                                <input
                                  type="checkbox"
                                  checked={!!t.saveToMyList}
                                  onChange={(e) => updatePax(rIdx, pIdx, { saveToMyList: e.target.checked })}
                                  data-testid={`hotel-booking-save-traveller-${rIdx}-${pIdx}`}
                                  className="w-4 h-4 rounded"
                                />
                                <BookmarkPlus className="w-3.5 h-3.5 text-[#659A9B]" />
                                Save this traveller to my list for faster booking next time
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4">
                <h2 className="text-white font-bold">Contact Information</h2>
              </div>
              <div className="p-6">
                <p className="text-xs text-gray-500 mb-4">We&apos;ll send booking confirmation, hotel voucher and updates to these.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-700 mb-1 block">Email *</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="email"
                        placeholder="you@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="hotel-booking-email"
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700 mb-1 block">Mobile *</label>
                    <div className="flex gap-1.5">
                      <select value={dialCode} onChange={(e) => setDialCode(e.target.value)} data-testid="hotel-booking-dialcode" className="px-2 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#659A9B]">
                        <option value="+91">+91</option>
                        <option value="+1">+1</option>
                        <option value="+44">+44</option>
                        <option value="+971">+971</option>
                        <option value="+65">+65</option>
                      </select>
                      <div className="relative flex-1">
                        <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="tel"
                          placeholder="9876543210"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          data-testid="hotel-booking-phone"
                          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {!isB2C && !isAgent && (
                  <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                    <UserPlus className="w-3 h-3" />
                    <button type="button" className="text-[#659A9B] font-bold hover:underline" onClick={() => navigate('/login')}>Login</button>
                    {' '}to use your saved travellers and faster checkout.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ── Right sticky sidebar ── */}
          <div className="lg:w-96 flex-shrink-0">
            <div className="space-y-4 lg:sticky lg:top-20">

              {isAgent && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-testid="hotel-booking-payment-mode-card">
                  <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4">
                    <h3 className="text-white font-bold">Payment Mode</h3>
                  </div>
                  <div className="p-4 space-y-2">
                    {[
                      { id: 'wallet', label: 'Wallet', sub: `Balance ₹${Number(agent?.wallet_balance || 0).toLocaleString('en-IN')}`, icon: Wallet },
                      ...((agent?.agent_payment_capabilities?.credit_enabled || ['CREDIT', 'HYBRID'].includes(agent?.payment_mode)) ? [{ id: 'credit', label: 'Credit Line', sub: `Available ₹${Number(agent?.credit_available || 0).toLocaleString('en-IN')}`, icon: CreditCard }] : []),
                      { id: 'gateway', label: 'Payment Gateway', sub: 'Pay via card / UPI / Netbanking', icon: Globe },
                      { id: 'hold', label: 'Hold Booking', sub: 'Reserve now, pay before deadline', icon: Clock },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setPaymentMode(m.id)}
                        data-testid={`hotel-booking-pay-${m.id}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${paymentMode === m.id ? 'border-[#659A9B] bg-[#659A9B]/10' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <m.icon className={`w-4 h-4 shrink-0 ${paymentMode === m.id ? 'text-[#1B375B]' : 'text-gray-500'}`} />
                        <div className="flex-1">
                          <p className={`text-sm font-bold ${paymentMode === m.id ? 'text-[#1B375B]' : 'text-gray-700'}`}>{m.label}</p>
                          <p className="text-xs text-gray-500">{m.sub}</p>
                        </div>
                        {paymentMode === m.id && <div className="w-2 h-2 rounded-full bg-[#1B375B]" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4">
                  <h3 className="text-white font-bold">Fare Summary</h3>
                </div>
                <div className="p-6 space-y-3">
                  <div className="text-xs text-gray-500 pb-2 border-b border-gray-100">
                    <span className="font-medium text-gray-700">Travelers:</span> {totalPax.adults} Adult{totalPax.adults !== 1 ? 's' : ''}{totalPax.children ? `, ${totalPax.children} Child` : ''}
                  </div>

                  {/* Phase H6.C — Per-room summary so user double-checks the supplier
                      bundle (e.g. "3 rooms" = 3× Deluxe King) before paying. */}
                  {roomsInOption.length > 0 && (
                    <div className="pb-2 border-b border-gray-100 space-y-1.5" data-testid="hotel-booking-rooms-summary">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#659A9B]">
                        {roomsInOption.length} room{roomsInOption.length !== 1 ? 's' : ''} in this booking
                      </p>
                      {roomsInOption.map((ri, riIdx) => {
                        const _rType = ri.rt || ri.rc || `Room ${riIdx + 1}`;
                        const _adt = ri.adt || 0;
                        const _chd = ri.chd || 0;
                        const _mb = ri.mb;
                        const _bt = ri.bt || ri.bedType;
                        return (
                          <div key={riIdx} className="flex items-start gap-2 text-[11px] text-gray-600 leading-snug" data-testid={`hotel-booking-room-summary-${riIdx}`}>
                            <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-extrabold bg-[#1B375B] text-white shrink-0 mt-0.5">{riIdx + 1}</span>
                            <span className="min-w-0">
                              <span className="font-bold text-[#1B375B] block leading-tight">{_rType}</span>
                              <span className="text-gray-500">
                                {_bt && <>{_bt} · </>}{_adt} adult{_adt !== 1 ? 's' : ''}{_chd ? `, ${_chd} child${_chd !== 1 ? 'ren' : ''}` : ''}
                                {_mb && <span className="text-amber-700"> · {Array.isArray(_mb) ? _mb.join(', ') : _mb}</span>}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                      {optionMeta.refundable && (
                        <p className="text-[10px] text-emerald-700 font-semibold flex items-center gap-1 pt-1">
                          <ShieldCheck className="w-3 h-3" /> Free cancellation included
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Room Total</span>
                    <span className="font-semibold">₹{(liveTotalAmount || totalAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Taxes & Fees</span>
                    <span className="font-semibold text-gray-700">Included</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 flex items-end justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">You Pay</p>
                      <p className="text-[10px] text-gray-400">{paymentMode === 'hold' ? 'No charge now' : 'Inclusive of taxes'}</p>
                    </div>
                    <span className="text-2xl font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hotel-booking-you-pay">
                      ₹{(liveTotalAmount || totalAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" data-testid="hotel-booking-error">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-red-700 font-semibold">{error}</p>
                    </div>
                  )}

                  <button
                    onClick={handleSubmit}
                    disabled={submitting || watchdogSoldout || (watchdogFareChange && !watchdogFareChange.acceptedAt)}
                    data-testid="hotel-booking-submit"
                    className="w-full py-3 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {submitting
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> {paymentMode === 'hold' ? 'Holding…' : 'Confirming…'}</>
                      : watchdogSoldout
                        ? <>Unavailable</>
                        : <>{paymentMode === 'hold' ? 'Hold This Booking' : 'Confirm & Pay'}</>}
                  </button>

                  <p className="text-[11px] text-center text-gray-400">
                    {paymentMode === 'hold' ? 'Hold expires at supplier deadline. Pay from My Bookings before then.' : "By clicking Confirm, you accept the hotel's policies."}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
      <BottomNav />
    </div>
  );
};

export default HotelBookingPage;
